import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import {
  AUTHENTICATION_SCOPES, ErrorHandler, Text, useCreateBillingPortalMutation,
} from '@playbookux/playbook-web-core';
import {
  Column,
  Grid,
  Row,
  Link,
} from '../../pages/project/study/styles';
import {
  NoResultsMessage, Dropdown,
} from './styles';
import ProjectCard from '../project-card';
import {
  GET_COMPANY_MEMBERS,
  ADD_PROJECT_MEMBER,
  REMOVE_PROJECT_MEMBER,
} from './graphql';
import { useUserScope } from '../../../core/hooks';

const Collaborators = (props) => {
  const {
    companyId, project, plan, stripeCustomerId,
  } = props;
  const {
    data: companyMembersData = {},
    errors: companyMembersErrors,
    loading: companyMembersLoading,
  } = useQuery(GET_COMPANY_MEMBERS, {
    variables: {
      companyId,
    },
    skip: !companyId,
  });
  const companyMembers = useMemo(() => companyMembersData.companyMembers || [], [companyMembersLoading]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [addProjectMember] = useMutation(ADD_PROJECT_MEMBER);
  const [removeProjectMember] = useMutation(REMOVE_PROJECT_MEMBER);
  const errors = companyMembersErrors;

  const scope = useUserScope();

  const {
    createPortal,
  } = useCreateBillingPortalMutation();

  useEffect(() => {
    if (companyMembers.length > 0) {
      setFilteredMembers(companyMembers.reduce((acc, member) => {
        if (member.user.id !== project.createdBy?.id) {
          acc.push({
            key: member.user.id,
            text: `${member.user.profile.firstName} ${member.user.profile.lastName}`,
            value: member.user.id,
          });
        }
        return acc;
      }, []));
    }
  }, [companyMembers]);

  useEffect(() => {
    if (project.members?.length > 0) {
      project.members.forEach((member) => {
        setSelectedMembers((prev) => ([...prev, member.id]));
      });
    }
  }, []);

  const onChangeHandler = (e, { value }) => {
    // disable to remove users
    if (scope === AUTHENTICATION_SCOPES.SHARE_PROJECT) return;
    setSelectedMembers(() => value);
    const toRemove = selectedMembers.filter((member) => !value.includes(member));
    toRemove.forEach((memberId) => {
      removeProjectMember({
        variables: {
          projectId: project.id,
          userId: memberId,
        },
      });
    });
  };

  const onBlurHandler = () => {
    // disable to add users
    if (scope === AUTHENTICATION_SCOPES.SHARE_PROJECT) return;
    if (selectedMembers.length > 0) {
      selectedMembers.forEach((memberId) => {
        addProjectMember({
          variables: {
            projectId: project.id,
            userId: memberId,
          },
        });
      });
    }
  };

  const handleBillingPortal = () => {
    createPortal(stripeCustomerId)
      .then((response) => {
        if (response) {
          window.open(response);
        }
      });
  };

  if (errors) {
    return (
      <ErrorHandler
        errors={errors}
        defaultMessage="Error while retrieving data."
      />
    );
  }

  return (
    <>
      <ProjectCard
        title="Collaborators"
        hasUpgrade={plan !== 'scale'}
        upgradeHandler={handleBillingPortal}
        noLHeight={plan !== 'scale'}
      >
        <Grid columns={1}>
          <Row>
            <Column computer={16} tablet={16} mobile={16}>
              <Text>
                Do you want to invite collaborators to this project?
                <br />
                Collaborators will receive email &amp; calendar notifications for this project.
              </Text>
              <Dropdown
                placeholder="Select a team member"
                options={filteredMembers}
                fluid
                multiple
                selection
                search={plan === 'scale'}
                onChange={onChangeHandler}
                onBlur={onBlurHandler}
                MODERATED
                noResultsMessage={(
                  <NoResultsMessage>
                    You don&apos;t have any other team members in PlaybookUX
                    <Link href="/account">Invite team members</Link>
                  </NoResultsMessage>
                )}
                value={selectedMembers}
                disabled={plan !== 'scale' || scope === AUTHENTICATION_SCOPES.SHARE_PROJECT}
              />
            </Column>
          </Row>
        </Grid>
      </ProjectCard>
    </>

  );
};

Collaborators.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string,
    members: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
    })),
    createdBy: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  companyId: PropTypes.string.isRequired,
  plan: PropTypes.string.isRequired,
  stripeCustomerId: PropTypes.string.isRequired,
};

export default Collaborators;
